import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Canceler } from "axios";
import { useAppTable, useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import { Button, Hide, VStack, Text, useToast } from "@chakra-ui/react";
import { AppIcon, AppPageContent, AppPageHeader } from "../../../components";
import { InternshipScheduleStudentSkillList as ListComponent } from "./InternshipScheduleStudentSkillList";
import {
    StudentSkill as Entity,
    StudentSkillApi as EntityApi,
    Internship,
    InternshipApi,
    InternshipSchedule,
    InternshipScheduleApi,
    InternshipScheduleSeat,
    InternshipScheduleSeatApi,
    InternshipScheduleSkill,
    InternshipScheduleSkillApi,
    SkillCriteria,
    SkillCriteriaApi,
    Training,
    User,
    UserApi,
} from "../../../apis";
import { APPC, cPath } from "../../../config";

export const InternshipScheduleStudentSkillPage = () => {
    // hook
    const { t } = useTrans();
    const { user: loggedInUser, grant } = useAuthData();
    const toast = useToast();
    const {
        id: internshipId,
        internshipScheduleId,
        studentId,
        internshipScheduleSeatId,
    } = useParams<{
        id: string;
        internshipScheduleId: string;
        studentId: string;
        internshipScheduleSeatId: string;
    }>();

    // state & const
    const [loading, setLoading] = useState(true);
    const [loadingList, setLoadingList] = useState(true);
    const [internship, setInternship] = useState<Internship | null>(null);
    const [internshipSchedule, setInternshipSchedule] =
        useState<InternshipSchedule | null>(null);
    const [internshipScheduleIdState, setInternshipScheduleIdState] = useState<
        string | undefined
    >(internshipScheduleId);
    const [user, setUser] = useState<User | null>(null);
    const [skillCriterias, setSkillCriterias] = useState<any[]>([]);
    const [assignListRatesLoaded, setAssignListRatesLoaded] =
        useState<boolean>(false);
    const [assignListRates, setAssignListRates] = useState({});
    const [internshipScheduleSeat, setInternshipScheduleSeat] =
        useState<InternshipScheduleSeat>();
    const [assignListGesturesLoaded, setAssignListGesturesLoaded] =
        useState<boolean>(false);
    const [assignListGestures, setAssignListGestures] = useState({});

    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    const isCompany =
        window.location.pathname ===
        cPath("ADMIN.INTERNSHIP_SCHEDULE_STUDENT_SKILL_PAGE", {
            id: internshipId,
            internshipScheduleId,
            studentId,
        });

    const isStudent =
        window.location.pathname ===
        cPath("ADMIN.STUDENT_SKILL_PAGE", {
            internshipScheduleSeatId,
        });

    const { table, pageIndex, pagesCount, pageSize, setPagesCount } =
        useAppTable("name", "asc", APPC.PAGE_SIZES[3]);

    const paging = {
        enabled: true,
        pageSizes: APPC.PAGE_SIZES,
        pageSize,
        pageIndex,
        pagesCount,
    };

    // handler
    const loadAssignListGestures = async () => {
        const params = {
            [`order[skillCriteria.skill.name]`]: "asc",
            pagination: false,
        };
        if (internshipScheduleId) {
            params[`internshipSchedule.id`] = internshipScheduleId;
        } else if (internshipScheduleSeatId) {
            params[`internshipSchedule.id`] = (
                internshipScheduleSeat?.internshipSchedule as InternshipSchedule
            )?.id;
            setInternshipScheduleIdState(params[`internshipSchedule.id`]);
        }

        await InternshipScheduleSkillApi.getCollection<InternshipScheduleSkill>(
            1,
            params
        ).then(({ errorMessage, response }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                const gestureMap = {};
                response.items.forEach((item) => {
                    const criteriaId = (item?.skillCriteria as SkillCriteria)
                        ?.id;
                    gestureMap[criteriaId] = {
                        gestures: item.gestures,
                    };
                });
                setAssignListGestures(gestureMap);
            }
        });
        setAssignListGesturesLoaded(true);
    };

    const loadAssignListRates = async () => {
        const params = {
            [`order[skillCriteria.skill.name]`]: "asc",
            pagination: false,
        };
        if (internshipScheduleId && studentId) {
            params["internshipSchedule.id"] = Number(internshipScheduleId);
            params["student.id"] = Number(studentId);
        } else if (internshipScheduleSeatId && studentId) {
            params["internshipSchedule.id"] = (
                internshipScheduleSeat?.internshipSchedule as InternshipSchedule
            )?.id;
            params["student.id"] = Number(studentId);
        } else if (internshipScheduleSeatId && loggedInUser) {
            params["internshipSchedule.id"] = (
                internshipScheduleSeat?.internshipSchedule as InternshipSchedule
            )?.id;
            params["student.id"] = loggedInUser.id;
        }

        await EntityApi.getCollection<Entity>(1, params).then(
            ({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    const rateMap = {};
                    response.items.forEach((item) => {
                        const criteriaId = EntityApi.resourceIRItoId(
                            item.skillCriteria as string
                        );
                        rateMap[criteriaId] = {
                            rate: item.rate,
                            finalRate: item.finalRate,
                            studentSkillId: item.id,
                        };
                    });
                    setAssignListRates(rateMap);
                }
            }
        );
        setAssignListRatesLoaded(true);
    };

    const loadSkillCriterias = () => {
        setLoadingList(true);
        const params = {
            itemsPerPage: pageSize,
            "skill.training.id": `${(internship?.training as Training).id}`,
            [`order[skill.name + 0]`]: "asc",
            [`order[name + 0]`]: "asc",
        };

        SkillCriteriaApi.getCollection<SkillCriteria>(
            pageIndex + 1,
            params,
            (c) => {
                cancelTokenSourcesRef.current.push(c);
            }
        )
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    const updatedSkillCriterias = response.items.map(
                        (criteria) => {
                            let rate: { value: string; label: string } | null =
                                null;
                            let finalRate: {
                                value: string;
                                label: string;
                            } | null = null;
                            let studentSkillId = 0;
                            let gestures = [];

                            const foundRates =
                                assignListRates[Number(criteria.id)];
                            if (foundRates) {
                                rate = {
                                    value: foundRates.rate,
                                    label: t(
                                        `con.StudentSkill:RATE_${foundRates.rate}`
                                    ),
                                };
                                finalRate = {
                                    value: foundRates.finalRate,
                                    label: t(
                                        `con.StudentSkill:RATE_${foundRates.finalRate}`
                                    ),
                                };
                                studentSkillId = foundRates.studentSkillId;
                            }

                            const foundGestures =
                                assignListGestures[Number(criteria.id)];
                            if (foundGestures) {
                                gestures = foundGestures.gestures;
                            }

                            return {
                                ...criteria,
                                rate,
                                finalRate,
                                studentSkillId,
                                gestures,
                            };
                        }
                    );
                    setSkillCriterias(updatedSkillCriterias);
                }
            })
            .finally(() => setLoadingList(false));
    };

    const downloadSkillReportHandler = (
        internshipScheduleId: number,
        skillStudentId: number
    ) => {
        if (internshipScheduleId > 0) {
            InternshipScheduleApi.postCollectionSkillReport<
                BlobPart,
                { internshipScheduleId: number; skillStudentId: number }
            >({
                internshipScheduleId,
                skillStudentId,
            }).then(({ response }) => {
                if (response !== null) {
                    const fileURL = URL.createObjectURL(
                        new Blob([response], { type: "application/pdf" })
                    );
                    const alink = document.createElement("a");
                    alink.href = fileURL;
                    alink.download = "skill-report.pdf";
                    alink.click();
                }
            });
        }
    };

    // useEffect
    useEffect(() => {
        if (!loading) {
            loadAssignListRates();
            loadAssignListGestures();
        }
    }, [loading]);

    useEffect(() => {
        if (assignListRatesLoaded && assignListGesturesLoaded && internship) {
            loadSkillCriterias();
        }
    }, [
        pageIndex,
        pageSize,
        assignListRatesLoaded,
        assignListGesturesLoaded,
        internship,
    ]);

    useEffect(() => {
        if (isCompany) {
            setLoading(true);
            Promise.all([
                internshipId
                    ? InternshipApi.getItem<Internship>(internshipId)
                    : Promise.resolve(null),
                internshipScheduleId
                    ? InternshipScheduleApi.getItem<InternshipSchedule>(
                          internshipScheduleId
                      )
                    : Promise.resolve(null),
                studentId
                    ? UserApi.getItemStudent<User>(studentId)
                    : Promise.resolve(null),
            ])
                .then(([internshipRes, scheduleRes, userRes]) => {
                    if (internshipRes) {
                        if (internshipRes.errorMessage) {
                            toast({
                                title: internshipRes.errorMessage,
                                status: "error",
                            });
                        } else if (internshipRes.isNotFound) {
                            toast({
                                title: t("cmn:message.entityNotFound"),
                                status: "error",
                            });
                        } else if (internshipRes.response !== null) {
                            setInternship(internshipRes.response);
                        }
                    }
                    if (scheduleRes) {
                        if (scheduleRes.errorMessage) {
                            toast({
                                title: scheduleRes.errorMessage,
                                status: "error",
                            });
                        } else if (scheduleRes.isNotFound) {
                            toast({
                                title: t("cmn:message.entityNotFound"),
                                status: "error",
                            });
                        } else if (scheduleRes.response !== null) {
                            setInternshipSchedule(scheduleRes.response);
                        }
                    }
                    if (userRes) {
                        if (userRes.errorMessage) {
                            toast({
                                title: userRes.errorMessage,
                                status: "error",
                            });
                        } else if (userRes.isNotFound) {
                            toast({
                                title: t("cmn:message.entityNotFound"),
                                status: "error",
                            });
                        } else if (userRes.response !== null) {
                            setUser(userRes.response);
                        }
                    }
                })
                .finally(() => setLoading(false));
        } else if (isStudent && internshipScheduleSeatId) {
            setLoading(true);
            InternshipScheduleSeatApi.getItemMyInternship<InternshipScheduleSeat>(
                internshipScheduleSeatId
            )
                .then(({ errorMessage, isNotFound, response }) => {
                    if (errorMessage) {
                        toast({
                            title: errorMessage,
                            status: "error",
                        });
                    } else if (isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (response !== null) {
                        setInternshipScheduleSeat(response);
                        setInternship(
                            (response.internshipSchedule as InternshipSchedule)
                                .internship as Internship
                        );
                    }
                })
                .finally(() => setLoading(false));
        } else {
            setLoading(true);
            Promise.all([
                internshipScheduleSeatId
                    ? InternshipScheduleSeatApi.getItemMyInternship<InternshipScheduleSeat>(
                          internshipScheduleSeatId
                      )
                    : Promise.resolve(null),
                studentId
                    ? UserApi.getItemStudent<User>(studentId)
                    : Promise.resolve(null),
            ])
                .then(([internshipScheduleSeatRes, userRes]) => {
                    if (internshipScheduleSeatRes) {
                        if (internshipScheduleSeatRes.errorMessage) {
                            toast({
                                title: internshipScheduleSeatRes.errorMessage,
                                status: "error",
                            });
                        } else if (internshipScheduleSeatRes.isNotFound) {
                            toast({
                                title: t("cmn:message.entityNotFound"),
                                status: "error",
                            });
                        } else if (
                            internshipScheduleSeatRes.response !== null
                        ) {
                            setInternshipScheduleSeat(
                                internshipScheduleSeatRes.response
                            );
                            setInternship(
                                (
                                    internshipScheduleSeatRes.response
                                        .internshipSchedule as InternshipSchedule
                                ).internship as Internship
                            );
                        }
                    }
                    if (userRes) {
                        if (userRes.errorMessage) {
                            toast({
                                title: userRes.errorMessage,
                                status: "error",
                            });
                        } else if (userRes.isNotFound) {
                            toast({
                                title: t("cmn:message.entityNotFound"),
                                status: "error",
                            });
                        } else if (userRes.response !== null) {
                            setUser(userRes.response);
                        }
                    }
                })
                .finally(() => setLoading(false));
        }
    }, [
        internshipId,
        internshipScheduleId,
        internshipScheduleSeatId,
        studentId,
    ]);

    return (
        <>
            <AppPageHeader
                title={t(
                    "padm.InternshipScheduleStudentSkillPage:text.pageTitle"
                )}
                breadcrumb={
                    internship?.name &&
                    internshipSchedule?.name &&
                    user?.fullName &&
                    isCompany
                        ? [
                              {
                                  label: internship?.name,
                                  url: cPath("ADMIN.INTERNSHIP_PAGE"),
                              },
                              {
                                  label: internshipSchedule?.name,
                                  url: cPath("ADMIN.INTERNSHIP_SCHEDULE_PAGE", {
                                      id: internshipId,
                                  }),
                              },
                              {
                                  label: user?.fullName,
                                  url: cPath(
                                      "ADMIN.INTERNSHIP_SCHEDULE_STUDENT_PAGE",
                                      {
                                          id: internshipId,
                                          internshipScheduleId:
                                              internshipScheduleId,
                                      }
                                  ),
                              },
                          ]
                        : internshipScheduleSeat && isStudent
                          ? [
                                {
                                    label: `${
                                        (
                                            (
                                                internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                                            )?.internship as Internship
                                        ).name
                                    } (${
                                        (
                                            internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                                        ).name
                                    })`,
                                    url: cPath("ADMIN.MY_INTERNSHIP_PAGE"),
                                },
                            ]
                          : internshipScheduleSeat && user?.fullName
                            ? [
                                  {
                                      label: `${
                                          (
                                              (
                                                  internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                                              )?.internship as Internship
                                          ).name
                                      } (${
                                          (
                                              internshipScheduleSeat?.internshipSchedule as InternshipSchedule
                                          ).name
                                      })`,
                                      url: cPath("ADMIN.MY_INTERNSHIP_PAGE"),
                                  },
                                  {
                                      label: user?.fullName,
                                      url: cPath("ADMIN.STUDENTS_PAGE", {
                                          internshipScheduleSeatId,
                                      }),
                                  },
                              ]
                            : []
                }
                isLoading={loading}
            >
                {!grant.isCompanyDirector ? (
                    <Button
                        variant={"primary"}
                        onClick={() => {
                            downloadSkillReportHandler(
                                Number(internshipScheduleIdState),
                                Number(studentId ?? loggedInUser?.id)
                            );
                        }}
                    >
                        <AppIcon name="icl-file-pdf" w="1rem" />
                        <Hide below="md">
                            <Text ml={2}>
                                {t(
                                    "padm.InternshipScheduleStudentSkillPage:button.report"
                                )}
                            </Text>
                        </Hide>
                    </Button>
                ) : (
                    <></>
                )}
            </AppPageHeader>
            <AppPageContent>
                <VStack p={6}>
                    <ListComponent
                        table={table}
                        paging={paging}
                        list={skillCriterias}
                        setList={setSkillCriterias}
                        internshipScheduleSeat={
                            internshipScheduleSeat as InternshipScheduleSeat
                        }
                        loadingList={loadingList}
                    />
                </VStack>
            </AppPageContent>
        </>
    );
};
